import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, Copy, Clock } from "@phosphor-icons/react";
import { PageHeader } from "../components/ui/kaizen";

export default function EmailsModule() {
  const [mode, setMode] = useState("draft");
  const [form, setForm] = useState({ client_name: "", context: "", tone: "professionnel", intent: "nouvelle_reponse" });
  const [followup, setFollowup] = useState({ client_name: "", client_last_message: "" });
  const [output, setOutput] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get("/emails").then(r => setHistory(r.data)).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const submit = async () => {
    setBusy(true); setOutput("");
    try {
      const url = mode === "draft" ? "/emails/draft" : "/emails/followup";
      const payload = mode === "draft" ? form : followup;
      const { data } = await api.post(url, payload);
      setOutput(data.draft);
      loadHistory();
      toast.success("Courriel généré");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const copy = () => { navigator.clipboard.writeText(output); toast.success("Copié !"); };

  return (
    <div data-testid="emails-page">
      <div className="px-8 lg:px-12 pt-8">
        <PageHeader title={<>Assistant Courriels IA</>} subtitle={<>Rédige, réponds, fais le suivi avec tes clients — en français du Québec.</>} />
      </div>

      <div className="px-8 lg:px-12 py-8 grid grid-cols-1 lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          <div className="flex gap-0 hard-border mb-6">
            <button data-testid="tab-draft" onClick={()=>setMode("draft")} className={`flex-1 py-3 ticker transition-colors ${mode==="draft"?"bg-foreground text-white":"hover:bg-secondary"}`}>Rédaction</button>
            <button data-testid="tab-followup" onClick={()=>setMode("followup")} className={`flex-1 py-3 ticker transition-colors ${mode==="followup"?"bg-foreground text-white":"hover:bg-secondary"}`}>Suivi client</button>
          </div>

          {mode === "draft" ? (
            <div className="space-y-4">
              <Field label="Nom du client" val={form.client_name} on={(v)=>setForm({...form, client_name:v})} testId="client-name-input"/>
              <div className="grid grid-cols-2 gap-4">
                <Select label="Intention" val={form.intent} on={(v)=>setForm({...form, intent:v})} testId="intent-select" options={[
                  ["nouvelle_reponse","Nouvelle réponse"],["suivi","Suivi"],["remerciement","Remerciement"],["relance","Relance"]
                ]}/>
                <Select label="Ton" val={form.tone} on={(v)=>setForm({...form, tone:v})} testId="tone-select" options={[
                  ["professionnel","Professionnel"],["chaleureux","Chaleureux"],["bref","Bref"],["enthousiaste","Enthousiaste"]
                ]}/>
              </div>
              <TextArea label="Contexte / message reçu" val={form.context} on={(v)=>setForm({...form, context:v})} testId="context-textarea"/>
            </div>
          ) : (
            <div className="space-y-4">
              <Field label="Nom du client" val={followup.client_name} on={(v)=>setFollowup({...followup, client_name:v})} testId="fu-client-input"/>
              <TextArea label={`Dernier message du client (ex: "Bravo, très bien")`} val={followup.client_last_message} on={(v)=>setFollowup({...followup, client_last_message:v})} testId="fu-message-textarea"/>
            </div>
          )}

          <button data-testid="generate-email-btn" onClick={submit} disabled={busy}
            className="mt-6 bg-[#D90429] hover:bg-[#BF0424] text-white px-6 py-3 ticker flex items-center gap-2 disabled:opacity-60 transition-colors">
            <PaperPlaneTilt size={16}/> {busy ? "Génération…" : "Générer le courriel"}
          </button>

          {output && (
            <div className="mt-8 hard-border" data-testid="email-output">
              <div className="flex items-center justify-between hard-border-b px-5 py-3 bg-secondary/40">
                <div className="ticker">Brouillon</div>
                <button onClick={copy} className="ticker flex items-center gap-1.5 hover:text-[#D90429]" data-testid="copy-email-btn"><Copy size={14}/> Copier</button>
              </div>
              <pre className="whitespace-pre-wrap p-6 text-sm leading-relaxed font-[Satoshi]">{output}</pre>
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          <div className="ticker text-muted-foreground mb-4 flex items-center gap-2"><Clock size={14}/> Historique</div>
          <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2" data-testid="email-history">
            {history.length === 0 && <div className="text-sm text-muted-foreground">Aucun courriel généré.</div>}
            {history.map((e) => (
              <div key={e.id} className="hard-border p-4 hover:bg-secondary/30 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-sm">{e.client_name}</div>
                  <span className="ticker text-muted-foreground">{e.intent}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1 line-clamp-3">{e.draft.slice(0, 160)}…</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, val, on, testId }) {
  return (
    <div>
      <label className="ticker text-muted-foreground block mb-2">{label}</label>
      <input data-testid={testId} value={val} onChange={(e)=>on(e.target.value)}
        className="w-full hard-border bg-white px-4 py-2.5 focus:border-foreground outline-none transition-colors"/>
    </div>
  );
}
function TextArea({ label, val, on, testId }) {
  return (
    <div>
      <label className="ticker text-muted-foreground block mb-2">{label}</label>
      <textarea data-testid={testId} rows={6} value={val} onChange={(e)=>on(e.target.value)}
        className="w-full hard-border bg-white px-4 py-3 focus:border-foreground outline-none transition-colors resize-y"/>
    </div>
  );
}
function Select({ label, val, on, options, testId }) {
  return (
    <div>
      <label className="ticker text-muted-foreground block mb-2">{label}</label>
      <select data-testid={testId} value={val} onChange={(e)=>on(e.target.value)}
        className="w-full hard-border bg-white px-4 py-2.5 focus:border-foreground outline-none transition-colors">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
